import { useState } from 'react'
import { stringify } from 'yaml'
import styles from './ConfigHelpPanel.module.css'

type Tab = 'global' | 'node'

interface SnippetCard {
  title: string
  description: string
  code: string
  group?: string
  language?: string
}

function yamlSnippet(value: unknown): string {
  return stringify(value, { indent: 2, lineWidth: 0 })
}

const GLOBAL_SNIPPETS: SnippetCard[] = [
  {
    title: 'Upload 접속 설정',
    description: '공통 FTP 접속 정보. 카메라별 예외 경로는 Node 탭의 base_fallback_dirs에 둡니다.',
    group: '필수',
    code: yamlSnippet({
      schema_version: 2,
      upload: {
        protocol: 'ftp',
        host: 'ftp.example.com',
        port: 21,
        user: 'uploader',
        password: 'secret:ftp-password:global:global',
        chunk_size: 104857600,
        max_retry: 5,
        bandwidth_limit_mbps: 0,
      },
    }),
  },
  {
    title: 'Secret reference',
    description: '평문 대신 secret reference를 사용하면 시크릿 페이지에서 중앙 관리됩니다.',
    group: '필수',
    code: yamlSnippet({ upload: { password: 'secret:ftp-password:global:global' } }),
  },
  {
    title: 'Polling 주기',
    description: '노드의 heartbeat / 명령 polling 간격(초). 평소 default 유지 권장.',
    group: '고급',
    code: yamlSnippet({ heartbeat_interval_sec: 15, command_poll_interval_sec: 5 }),
  },
]
// Note: retention(metrics/events 보관일)은 server 환경변수(NODE_METRICS_RETENTION_DAYS,
//       EVENTS_RETENTION_DAYS)로 관리됩니다.

const NODE_SNIPPETS: SnippetCard[] = [
  {
    title: '필수 Node Override 구조',
    description: '카메라, 녹화, 업로드를 v2 canonical tree로 구성합니다.',
    group: '필수',
    code: yamlSnippet({
      schema_version: 2,
      cameras: [
        { key: 'cam0', name: 'Camera 0', source: 'secret:rtsp-cam0:node:node-id' },
        { key: 'cam1', name: 'Camera 1', source: 'secret:rtsp-cam1:node:node-id' },
      ],
      recording: {
        disk_guard: { min_free_percent: 5 },
        // 예시를 그대로 적용해도 원본 해상도를 유지하도록 width와 height는 넣지 않는다.
        capture_defaults: { fps: 5, chunk_seconds: 1800 },
        scan: {
          extensions: ['.mp4'],
          recursive: false,
          min_file_age_seconds: 30,
          stable_required_count: 2,
          report_interval_seconds: 60,
          probe_metadata: true,
        },
        manual: { enabled: true, base_dir: '/data/manual' },
        inference: { enabled: true, base_dir: '/data/infer' },
      },
      upload: {
        base_dir: '/data/inbox',
        base_fallback_dirs: {
          cam0: '/VOL1/anno/C00004/BCT/line1_cam0',
          cam1: '/VOL1/anno/C00004/BCT/line1_cam1',
        },
        naming: { enabled: true, prefix: 'company__site__line' },
        auto: {
          manual: {
            enabled: false,
            schedule: { local_time: '02:00', timezone: 'Asia/Seoul' },
            file_window: 'previous_day',
            delete_local_after_success: false,
          },
          inference: {
            enabled: false,
            schedule: { local_time: '02:00', timezone: 'Asia/Seoul' },
            file_window: 'previous_day',
            delete_local_after_success: true,
          },
        },
      },
    }),
  },
  {
    title: 'Manual managed recording',
    description: '파일은 /data/manual/{camera_key}에 저장됩니다.',
    group: 'Manual',
    code: yamlSnippet({
      schema_version: 2,
      recording: {
        disk_guard: { min_free_percent: 5 },
        manual: { enabled: true, base_dir: '/data/manual' },
      },
    }),
  },
  {
    title: '카메라 1대 추가 시 같이 넣을 값',
    description: 'top-level cameras와 경로 예외 map에 같은 key를 사용합니다.',
    group: '카메라',
    code: yamlSnippet({
      schema_version: 2,
      cameras: [
        { key: 'cam2', name: 'Camera 2', source: 'secret:rtsp-cam2:node:node-id' },
      ],
      upload: {
        base_fallback_dirs: {
          cam2: '/VOL1/anno/C00004/BCT/line1_cam2',
        },
      },
    }),
  },
  {
    title: '카메라별 업로드 경로',
    description: 'naming을 쓰지 않을 때의 camera별 remote fallback 경로입니다.',
    group: '카메라',
    code: yamlSnippet({
      upload: {
        base_fallback_dirs: {
          cam0: '/VOL1/anno/C00004/BCT/line1_cam0',
          cam1: '/VOL1/anno/C00004/BCT/line1_cam1',
        },
      },
    }),
  },
  {
    title: '추론 녹화 설정',
    description: '외부 추론 프로세스가 생성한 {base_dir}/{camera_key} 파일을 발견합니다.',
    group: 'Inference',
    code: yamlSnippet({
      schema_version: 2,
      recording: {
        inference: { enabled: true, base_dir: '/data/infer' },
      },
    }),
  },
  {
    title: '에이전트 배포 템플릿',
    description: 'model.deploy가 사용할 agent-owned analysis 경로, GF token secret ref, camera별 slot config_target입니다.',
    group: 'Model Deploy',
    code: yamlSnippet({
      analysis: {
        service_name: 'guardian-alpha',
        analysis_node_name: 'node-name',
        root_dir: '/home/node/node-name',
        working_dir: '/home/node/node-name',
        config_path: '/home/node/node-name/node/node-name/config.yml',
        weights_dir: '/var/lib/node-agent/analysis/weights',
        model_slots_dir: '/var/lib/node-agent/analysis/model-slots',
        gf_token_ref: 'secret:gf-deploy-token:node:node-id',
        cameras: [
          {
            camera_key: 'cam0',
            name: 'Camera 0',
            external_aliases: [],
            config_target: {
              mode: 'top_level_weight_index',
              weight_index: 0,
              slot_format: 'openvino',
            },
          },
          {
            camera_key: 'cam1',
            name: 'Camera 1',
            external_aliases: [],
            config_target: {
              mode: 'top_level_weight_index',
              weight_index: 1,
              slot_format: 'openvino',
            },
          },
        ],
      },
    }),
  },
  {
    title: '다중 GuardianAlpha 배포 템플릿 (1~2개)',
    description: '공통 모델 저장소와 GF token은 analysis에 두고, 인스턴스별 경로·카메라는 guardian_nodes에 둡니다.',
    group: 'Model Deploy',
    code: yamlSnippet({
      analysis: {
        weights_dir: '/var/lib/node-agent/analysis/weights',
        model_slots_dir: '/var/lib/node-agent/analysis/model-slots',
        model_staging_dir: '/var/lib/node-agent/model-deployments/staging',
        gf_token_ref: 'secret:gf-deploy-token:node:node-id',
        guardian_nodes: [
          {
            analysis_node_name: 'alpha-a',
            config_path: '/home/node/alpha-a/node/alpha-a/config.yml',
            working_dir: '/home/node/alpha-a',
            cameras: [
              {
                camera_key: 'cam-a',
                name: 'Alpha A Camera',
                external_aliases: [],
                config_target: {
                  mode: 'top_level_weight_index',
                  weight_index: 0,
                  slot_format: 'openvino',
                },
              },
            ],
          },
          {
            analysis_node_name: 'alpha-b',
            config_path: '/home/node/alpha-b/node/alpha-b/config.yml',
            working_dir: '/home/node/alpha-b',
            cameras: [
              {
                camera_key: 'cam-b',
                name: 'Alpha B Camera',
                external_aliases: [],
                config_target: {
                  mode: 'top_level_weight_index',
                  weight_index: 0,
                  slot_format: 'openvino',
                },
              },
            ],
          },
        ],
      },
    }),
  },
  {
    title: 'RTSP Secret reference',
    description: 'managed recording camera source는 평문 RTSP 또는 secret reference를 사용할 수 있습니다.',
    group: '고급',
    code: yamlSnippet({
      schema_version: 2,
      cameras: [
        { key: 'cam0', name: 'Camera 0', source: 'secret:rtsp-cam0:node:node-id' },
      ],
    }),
  },
]

function getSnippets(tab: Tab): SnippetCard[] {
  if (tab === 'global') return GLOBAL_SNIPPETS
  return NODE_SNIPPETS
}

interface CopyButtonProps {
  code: string
}

function CopyButton({ code }: CopyButtonProps) {
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    }).catch(() => {
      // fallback: select text
    })
  }

  return (
    <button
      onClick={handleCopy}
      title="클립보드에 복사"
      className={`${styles.copyButton} ${copied ? styles.copyButtonCopied : ''}`}
    >
      {copied ? '✓ 복사됨' : '코드 복사'}
    </button>
  )
}

interface Props {
  activeTab: Tab
}

export default function ConfigHelpPanel({ activeTab }: Props) {
  const snippets = getSnippets(activeTab)
  const snippetGroups = snippets.reduce<Array<{ label: string; items: SnippetCard[] }>>((groups, snippet) => {
    const label = snippet.group ?? '기타'
    const existing = groups.find((group) => group.label === label)
    if (existing) {
      existing.items.push(snippet)
    } else {
      groups.push({ label, items: [snippet] })
    }
    return groups
  }, [])

  return (
    <div className={styles.panel}>
      <div className={styles.panelTitle}>도움말</div>

      {/* Tab-specific guide */}
      {activeTab === 'global' && <GlobalGuide />}
      {activeTab === 'node' && <NodeGuide />}

      {/* Snippet cards */}
      <div className={styles.snippetsSection}>
        <div className={styles.sectionLabel}>템플릿 스니펫</div>
        <div className={styles.snippetsList}>
          {snippetGroups.map((group, idx) => (
            <SnippetGroup
              key={group.label}
              title={group.label}
              snippets={group.items}
              defaultOpen={idx === 0}
            />
          ))}
        </div>
      </div>

      {/* Bottom UX guide */}
      <TroubleGuide />
    </div>
  )
}

function GlobalGuide() {
  return (
    <div className={styles.guideBox}>
      <div className={styles.guideHeader}>공통 설정 (모든 노드 공유)</div>
      <ul className={styles.guideList}>
        <li>heartbeat / command polling 주기</li>
        <li>upload host / user / password / chunk_size / bandwidth_limit_mbps</li>
        <li>schema_version과 공통 upload 기본값</li>
      </ul>
      <div className={styles.warnBox}>
        <strong>cameras와 upload.base_fallback_dirs는 여기 두지 마세요.</strong><br />
        Node 탭에서 노드별로 박아야 노드마다 다른 카메라/업로드 경로를 가질 수 있습니다.
      </div>
    </div>
  )
}

// 크기 생략은 해당 override만 제거하므로, 안내에서는 상위 설정 상속 규칙도 함께 설명한다.
function NodeGuide() {
  return (
    <div className={styles.guideBox}>
      <div className={styles.guideHeader}>노드별 설정 (이 노드만 적용)</div>
      <ul className={styles.guideList}>
        <li><strong>cameras</strong>: node camera inventory의 key/source</li>
        <li><strong>recording.manual / inference</strong>: 활성화와 local base_dir</li>
        <li><strong>recording.capture_defaults / scan</strong>: 공통 녹화·발견 정책</li>
        <li>해상도는 최종 적용 설정에 width·height가 모두 없으면 카메라 입력 크기를 유지합니다. 변환이 필요하면 두 값을 함께 지정합니다.</li>
        <li>Node Override에서 width·height를 삭제하거나 주석 처리해도 전체·현장 설정의 값은 상속됩니다. 주석 문구는 저장되지 않습니다.</li>
        <li><strong>upload.base_fallback_dirs</strong>: 같은 camera key별 remote 예외 경로</li>
      </ul>
      <div className={styles.infoBox}>
        녹화 실행 인자는 Node Detail의 Record 시작 팝업에서 설정합니다.
        Node Override에는 <strong>disk_guard.min_free_percent 5%</strong> 같은 노드 안전 기준을 둡니다.
      </div>
      <GuideDisclosure title="경로 규칙">
        로컬 파일은 <strong>recording.manual 또는 inference.base_dir/{'{camera_key}'}</strong>에 위치합니다.
        Upload naming 사용 시 remote base의 manual/infer inbox로 전송됩니다.
      </GuideDisclosure>
      <GuideDisclosure title="camera key 규칙">
        현장 스크립트가 보통 record0.sh/cam0처럼 0부터 시작하므로 신규 설정은 <strong>cam0, cam1</strong>을 권장합니다.
      </GuideDisclosure>
      <GuideDisclosure title="자동 업로드">
        Manual과 Inference 중 하나만 활성화할 수 있습니다. 시간대와 정각 단위 실행 시각은 노드 상세 화면에서 설정합니다.
      </GuideDisclosure>
      <GuideDisclosure title="merge 방식">
        전역 + 노드 설정이 deep merge되어 agent에게 내려갑니다. 노드별 카메라/source/경로는 Node Override에 둡니다.
      </GuideDisclosure>
    </div>
  )
}

function TroubleGuide() {
  return (
    <div className={styles.troubleBox}>
      <div className={styles.troubleTitle}>막혔을 때</div>
      <ul className={styles.troubleList}>
        <li>YAML 형식 오류면 빨간 표시. 표시된 line부터 확인하세요.</li>
        <li>저장 후 revision 번호가 +1 되면 OK.</li>
        <li>노드는 ~30초 이내 새 revision fetch (config_sync_sec=30).</li>
        <li>노드 적용 상태는 좌측 <strong>"Node Config State"</strong> 패널에서 확인.</li>
      </ul>
    </div>
  )
}

function SnippetCardItem({ card }: { card: SnippetCard }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className={styles.snippetCard}>
      <div
        className={styles.snippetHeader}
        onClick={() => setExpanded((v) => !v)}
      >
        <div className={styles.snippetContent}>
          <div className={styles.snippetTitle}>
            {expanded ? '▾' : '▸'} {card.title}
          </div>
          <div className={styles.snippetDescription}>{card.description}</div>
        </div>
        <CopyButton code={card.code} />
      </div>
      {expanded && (
        <pre className={styles.pre}>{card.code}</pre>
      )}
    </div>
  )
}

function SnippetGroup({
  title,
  snippets,
  defaultOpen,
}: {
  title: string
  snippets: SnippetCard[]
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(Boolean(defaultOpen))

  return (
    <div className={styles.snippetGroup}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={styles.snippetGroupButton}
      >
        <span>{open ? '▾' : '▸'} {title}</span>
        <span className={styles.snippetGroupCount}>{snippets.length}</span>
      </button>
      {open && (
        <div className={styles.snippetGroupBody}>
          {snippets.map((snippet) => (
            <SnippetCardItem key={snippet.title} card={snippet} />
          ))}
        </div>
      )}
    </div>
  )
}

function GuideDisclosure({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)

  return (
    <div className={styles.guideDisclosure}>
      <button onClick={() => setOpen((v) => !v)} className={styles.guideDisclosureButton}>
        {open ? '▾' : '▸'} {title}
      </button>
      {open && (
        <div className={styles.guideDisclosureBody}>{children}</div>
      )}
    </div>
  )
}
